import { useEffect, useRef } from 'react';
import { systemBus, EVENTS } from '../services/systemBus';

/**
 * [PURPOSE]: Automatically recalculate AC and senses when equipment changes
 * [WHY]: Equipping/unequipping armor, shields, or magical items should update character stats
 * [INTEGRATION]: Used in App.jsx to watch inventory array for equipped items
 *
 * @param {Array} inventory - Full inventory array
 * @param {Object} character - Character object
 * @param {Function} setCharacter - State setter for character (for senses updates)
 * @param {Function} logActivity - Activity logging function
 */
export const useItemEffects = (inventory, character, setCharacter, logActivity) => {
  // Track previous equipped items to detect changes
  const previousEquippedRef = useRef(null);

  useEffect(() => {
    // Get currently equipped items
    const equippedItems = inventory.filter(item => item.equipped);

    // Create signature of equipped items (ID + equipSlot)
    const currentSignature = equippedItems
      .map(item => `${item.id}:${item.equipSlot}`)
      .sort()
      .join('|');

    // Skip if no change detected
    if (previousEquippedRef.current === currentSignature) {
      return;
    }

    // Skip on initial mount (no previous value)
    if (previousEquippedRef.current === null) {
      previousEquippedRef.current = currentSignature;
      return;
    }

    // [FEATURE]: Equipment changed - determine what was added/removed
    const previousSignature = previousEquippedRef.current;
    const added = equippedItems.filter(item =>
      !previousSignature.includes(item.id)
    );
    const removed = previousSignature
      ? previousSignature.split('|')
          .map(sig => sig.split(':')[0])
          .filter(id => !currentSignature.includes(id))
      : [];

    // [FIX JAN 13, 2026]: Property-based detection instead of string matching (ARC-014)
    // Check for senses-granting items via effects array
    const sensesItems = equippedItems.filter(item =>
      item.effects?.some(e => e.target === 'darkvision' || e.target === 'truesight')
    );

    if (sensesItems.length > 0) {
      // Auto-update darkvision from magical items - read value from effects
      const additionalDarkvision = sensesItems.reduce((total, item) => {
        const darkvisionEffect = item.effects?.find(e => e.target === 'darkvision');
        if (darkvisionEffect && typeof darkvisionEffect.value === 'number') {
          return Math.max(total, darkvisionEffect.value);
        }
        return total;
      }, 0);

      if (additionalDarkvision > 0 && character) {
        const racialDarkvision = character.darkvision || 0;
        const totalDarkvision = Math.max(racialDarkvision, additionalDarkvision);

        if (character.darkvision !== totalDarkvision) {
          setCharacter(prev => ({
            ...prev,
            darkvision: totalDarkvision,
            darkvisionSource: additionalDarkvision > racialDarkvision ? 'magical item' : 'racial'
          }));
        }
      }
    }

    // [FIX JAN 13, 2026]: Property-based detection for speed items (ARC-014)
    // Check for speed-granting items via effects array
    const speedItems = equippedItems.filter(item =>
      item.effects?.some(e => e.type === 'speed' || e.target === 'flySpeed' || e.target === 'swimSpeed' || e.target === 'climbSpeed')
    );

    // [LOG]: Equipment changes to activity feed
    if (logActivity) {
      if (added.length > 0) {
        added.forEach(item => {
          const effects = [];
          if (item.acBonus) effects.push(`AC +${item.acBonus}`);
          if (item.magicalBonus) effects.push(`+${item.magicalBonus} magical bonus`);
          if (item.spells && item.spells.length > 0) effects.push(`${item.spells.length} spell(s)`);

          logActivity(
            `Equipped ${item.name}${effects.length > 0 ? ` (${effects.join(', ')})` : ''}`,
            'equipment',
            {
              itemName: item.name,
              slot: item.equipSlot,
              effects: effects.length > 0 ? effects : ['No special effects']
            }
          );
        });
      }

      if (removed.length > 0) {
        logActivity(
          `Unequipped ${removed.length} item(s)`,
          'equipment',
          {
            count: removed.length
          }
        );
      }
    }

    // [INFO]: AC recalculation is handled by calculateAC() in EquipmentPanel
    // We don't need to do it here since it's called every render with useMemo

    // [ARC-012 FIX]: Emit event so other systems know equipment changed
    systemBus.emit(EVENTS.EQUIPMENT_CHANGED, {
      addedItems: added.map(i => i.name),
      removedCount: removed.length,
      equippedCount: equippedItems.length,
      source: 'useItemEffects'
    });

    // Update ref for next comparison
    previousEquippedRef.current = currentSignature;

  }, [inventory, character, setCharacter, logActivity]);
};

export default useItemEffects;
